
import { useState } from 'react';
import { Link } from 'react-router-dom';

const PricingSection = () => {
  const [selectedFloor, setSelectedFloor] = useState<{from: number, to: number} | null>(null);

  const priceMatrix = [
    [3000, 3000, 3500, 3500, 4000, 4000],
    [3000, 3000, 3500, 3500, 4000, 4300],
    [3500, 3500, 3500, 3500, 4000, 4500],
    [3500, 3500, 3500, 3500, 4000, 4500],
    [4000, 4000, 4000, 4000, 4000, 4500],
    [4000, 4300, 4500, 4500, 4500, 5000],
  ];

  const additionalCharges = [
    {
      item: '行軍距離',
      price: '500起',
      icon: 'ri-walk-line',
      description: '車輛無法開進巷內需停在較遠處',
      color: 'teal'
    },
    {
      item: '轉乘電梯',
      price: '1,000',
      icon: 'ri-building-4-line',
      description: '需搭乘兩次電梯才能到達',
      color: 'emerald'
    },
    {
      item: '大型電視',
      price: '1,000起',
      icon: 'ri-tv-line',
      description: '電視尺寸超過55吋',
      color: 'teal'
    },
    {
      item: '夜間加成',
      price: '依時段',
      icon: 'ri-moon-line',
      description: '晚上20:00後服務',
      color: 'emerald'
    }
  ];

  const getPrice = (from: number, to: number) => {
    return priceMatrix[from][to];
  };

  return (
    <section id="pricing" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 標題區域 */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <i className="ri-price-tag-3-line text-lg"></i>
            <span>透明公開價格</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            新北搬家<span className="text-teal-600">價格表</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            <strong>建鈞搬家</strong>提供透明公開的價格標準，讓您清楚了解搬家費用
          </p>
        </div>

        {/* 電梯提醒卡片 */}
        <div className="max-w-4xl mx-auto mb-10">
          <div className="bg-gradient-to-r from-teal-500 to-emerald-500 rounded-2xl p-6 md:p-8 text-white shadow-xl">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className="ri-building-line text-4xl"></i>
              </div>
              <div className="text-center md:text-left flex-1">
                <h3 className="text-xl md:text-2xl font-bold mb-2">電梯大樓優惠價</h3>
                <p className="text-teal-100 mb-3">
                  如果<strong className="text-white">兩邊皆有電梯</strong>，一車搬家價格統一為
                </p>
                <div className="inline-flex items-baseline gap-1 bg-white/20 px-6 py-3 rounded-xl">
                  <span className="text-sm">NT$</span>
                  <span className="text-4xl font-bold">3,000</span>
                  <span className="text-sm">/車</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 價格表格 */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-4 md:p-6">
              <h3 className="text-white text-lg md:text-xl font-bold text-center flex items-center justify-center gap-2">
                <i className="ri-stairs-line"></i>
                無電梯樓層搬運價格表（以車計價）
              </h3>
            </div>
            
            <div className="p-4 md:p-6 overflow-x-auto">
              <table className="w-full min-w-[500px]">
                <thead>
                  <tr>
                    <th className="p-3 text-center bg-gray-100 rounded-tl-xl font-bold text-gray-700">
                      <div className="flex items-center justify-center gap-1">
                        <span>起點</span>
                        <i className="ri-arrow-right-line"></i>
                        <span>終點</span>
                      </div>
                    </th>
                    {[1, 2, 3, 4, 5, 6].map((floor) => (
                      <th key={floor} className={`p-3 text-center font-bold ${floor === 6 ? 'rounded-tr-xl' : ''} bg-teal-600 text-white`}>
                        {floor}樓
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[1, 2, 3, 4, 5, 6].map((fromFloor, rowIndex) => (
                    <tr key={fromFloor} className="hover:bg-teal-50/50 transition-colors">
                      <td className={`p-3 text-center font-bold bg-teal-600 text-white ${rowIndex === 5 ? 'rounded-bl-xl' : ''}`}>
                        {fromFloor}樓
                      </td>
                      {[0, 1, 2, 3, 4, 5].map((toFloor, colIndex) => {
                        const price = getPrice(rowIndex, toFloor);
                        const isSelected = selectedFloor?.from === rowIndex && selectedFloor?.to === toFloor;
                        return (
                          <td 
                            key={toFloor}
                            className={`p-3 text-center cursor-pointer transition-all ${
                              isSelected 
                                ? 'bg-teal-100 ring-2 ring-teal-500' 
                                : 'hover:bg-teal-50'
                            } ${rowIndex === 5 && colIndex === 5 ? 'rounded-br-xl' : ''}`}
                            onClick={() => setSelectedFloor({ from: rowIndex, to: toFloor })}
                          >
                            <span className={`font-semibold ${isSelected ? 'text-teal-700 text-lg' : 'text-gray-700'}`}>
                              {price.toLocaleString()}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 選擇結果顯示 */}
            {selectedFloor && (
              <div className="px-4 md:px-6 pb-6">
                <div className="bg-teal-50 rounded-xl p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center">
                      <i className="ri-calculator-line text-white"></i>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">您選擇的方案</div>
                      <div className="font-bold text-gray-900">
                        {selectedFloor.from + 1}樓 → {selectedFloor.to + 1}樓
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">預估費用</div>
                    <div className="text-2xl font-bold text-teal-600">
                      NT$ {getPrice(selectedFloor.from, selectedFloor.to).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 車輛規格說明 */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="bg-gradient-to-br from-gray-50 to-teal-50/50 rounded-2xl p-6 md:p-8 border border-gray-100">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-truck-line text-white text-xl"></i>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-1">車輛規格說明</h4>
                <p className="text-gray-600">一律使用 <strong className="text-teal-600">5噸搬家貨車</strong>（舊制3.5噸）</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-ruler-line text-teal-600"></i>
                  <span className="font-semibold text-gray-900">裝載高度</span>
                </div>
                <p className="text-gray-600 text-sm">從地面算起不超過 200cm</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-box-3-line text-teal-600"></i>
                  <span className="font-semibold text-gray-900">裝運容量</span>
                </div>
                <p className="text-gray-600 text-sm">高100×長300×寬160 cm</p>
              </div>
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <i className="ri-tools-line text-teal-600"></i>
                  <span className="font-semibold text-gray-900">隨車工具</span>
                </div>
                <p className="text-gray-600 text-sm">板車、膠膜、棉被等</p>
              </div>
            </div>
          </div>
        </div>

        {/* 特殊狀況加價 */}
        <div className="max-w-4xl mx-auto mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center flex items-center justify-center gap-2">
            <i className="ri-add-circle-line text-teal-600"></i>
            特殊狀況加價標準
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {additionalCharges.map((charge, index) => (
              <div 
                key={index} 
                className="bg-white rounded-2xl p-5 shadow-md hover:shadow-xl transition-all border border-gray-100 group"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                  charge.color === 'teal' 
                    ? 'bg-teal-100 text-teal-600' 
                    : 'bg-emerald-100 text-emerald-600'
                } group-hover:scale-110 transition-transform`}>
                  <i className={`${charge.icon} text-2xl`}></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-1">{charge.item}</h4>
                <p className="text-xs text-gray-500 mb-3">{charge.description}</p>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                  charge.color === 'teal' 
                    ? 'bg-teal-100 text-teal-700' 
                    : 'bg-emerald-100 text-emerald-700'
                }`}>
                  NT$ {charge.price}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 重要提醒 */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="bg-amber-50 border-l-4 border-amber-400 rounded-r-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-alert-line text-amber-600 text-xl"></i>
              </div>
              <div>
                <h4 className="font-bold text-gray-900 mb-2">重要提醒</h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  上述參考價格尚未納入<strong>特殊情況</strong>、<strong>距離</strong>、<strong>搬運日期</strong>、<strong>物品特殊性</strong>等因素。
                  如想了解更多，歡迎直接來電或 LINE 詢價，<strong className="text-teal-600">實際價格以客服人員報價為主</strong>。
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA 區域 */}
        <div className="text-center">
          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-3xl p-8 md:p-12 max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-customer-service-2-line text-white text-3xl"></i>
            </div>
            <h4 className="text-2xl font-bold text-gray-900 mb-3">需要詳細搬家報價？</h4>
            <p className="text-gray-600 mb-8">
              每個搬家案例都有其特殊性，我們提供免費現場評估，給您最準確的報價
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://line.me/ti/p/@285yavdh"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-green-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-green-700 transition-all shadow-lg hover:shadow-green-500/30 cursor-pointer whitespace-nowrap"
              >
                <img 
                  src="https://static.readdy.ai/image/9ef47c451bf9aafd7a63eea67af7d95d/c33a3c3f2fa31df7dfad05bfabc09369.webp" 
                  alt="LINE" 
                  className="w-5 h-5"
                />
                LINE 免費估價
              </a>
              <Link 
                to="/calculator"
                className="inline-flex items-center gap-2 bg-white border-2 border-teal-500 text-teal-600 px-8 py-4 rounded-full font-semibold hover:bg-teal-50 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calculator-line text-lg"></i>
                費用試算工具
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
